<template>
  <WdsModal
    :show="show"
    data-test="expense-report-delete-modal"
    no-close-icon
    @cancel="emit('cancel')"
  >
    <template #header>
      <div class="wds-modal-title">
        <div class="wds-modal-title--base" :title="deleteModalTitle">
          <WdsText xl2>
            {{ deleteModalTitle }}
          </WdsText>
          <template v-if="markedForDeletion?.deletableType === 'expense-report'">
            {{ $t(`This report and all associated expenses will be`) }}
            <span class="tw-font-bold">{{ $t('permanently deleted') }}.</span>
          </template>
        </div>
      </div>
    </template>
    <template v-if="markedForDeletion?.deletableType === 'expense-report'">
      {{
        $t(
          'To keep the expenses and submit them later, unlink it from this payment request instead',
        )
      }}
    </template>
    <template v-else>
      {{ $t(`This expense will be`) }}
      <span class="tw-font-bold">{{ $t('permanently deleted') }}.</span>
    </template>
    <template #footer>
      <WdsStack justify-space-between>
        <WdsButton tertiary data-test="expense-report-delete-cancel" @click="emit('cancel')">
          {{ $t('Cancel') }}
        </WdsButton>

        <WdsStack align-center gap-md>
          <WdsSpinner v-if="deletionInProgress" indeterminate />
          <WdsButton
            v-if="markedForDeletion?.deletableType === 'expense-report'"
            secondary
            :disabled="deletionInProgress"
            @click="emit('unlink')"
          >
            {{ $t('Unlink') }}
          </WdsButton>
          <WdsButton
            error
            :disabled="deletionInProgress"
            data-test="expense-report-delete-confirm"
            @click="performDeletion"
          >
            {{ $t('Delete') }}
          </WdsButton>
        </WdsStack>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import { WdsButton, WdsModal, WdsSpinner, WdsStack, WdsText } from '@worksome/wds-components'
import { computed, ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { useTranslation } from '@/composables/translation'

const props = defineProps<{
  show: boolean
  markedForDeletion?: {
    localId: number
    deletableType: 'expense' | 'expense-report'
  } | null
}>()

const { $t } = useTranslation()

const emit = defineEmits(['cancel', 'deleted', 'unlink'])

const deletionInProgress = ref(false)

const deleteModalTitle = computed(() =>
  props.markedForDeletion?.deletableType === 'expense-report'
    ? $t('Delete this expense report?')
    : $t('Delete expense'),
)

const { mutate: deleteExpenseReport } = useMutation(
  gql`
    mutation deleteExpenseReport($reportId: Int!) {
      deleteExpenseReport(localId: $reportId) {
        globalId
        localId
      }
    }
  `,
  () => ({
    variables: {
      reportId: props.markedForDeletion?.localId || '',
    },
  }),
)

const { mutate: deleteExpense } = useMutation(
  gql`
    mutation deleteExpense($expenseId: Int!) {
      deleteExpense(localId: $expenseId) {
        globalId
        localId
      }
    }
  `,
  () => ({
    variables: {
      expenseId: props.markedForDeletion?.localId || '',
    },
  }),
)

const performDeletion = async () => {
  deletionInProgress.value = true
  try {
    if (props.markedForDeletion?.deletableType === 'expense-report') {
      await deleteExpenseReport()
    } else {
      await deleteExpense()
    }
    emit('deleted')
  } finally {
    deletionInProgress.value = false
  }
}
</script>
